import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "conditionalField", "downPaymentInstallments", "downPaymentAmount", "maintenanceTax",
    "fundingInstallments", "fundingInstallmentsInput", "firstFundingDate", "paymentSection",
    "noProductMessage", "interestType", "interestTypeSelect", "beneficiariesSection", "adicionaisSection",
    "fillPriceButton", "fillPriceButtonText", "maintenanceTaxField",
    // Funerary sale type and discount fields
    "saleTypeSection", "discountLabel", "discountHint", "downPaymentAmountField", "discountField",
    "discountHintDefault", "discountHintPortability", "discountHintSeller", "discountHintExceeded", "discountAmountInput",
    // Sticky product summary
    "stickyProductSummary", "stickyProductName", "stickyProductType", "stickyProductPrice", "stickyProductPriceLabel",
    "stickyTotalEntry", "stickyTotalEntryValue",
    // New funerary plan targets
    "adhesionInstallmentsField", "adhesionInstallmentsInput", "adhesionInstallmentsMax", "adhesionInstallmentValue",
    "contractFirstDueDateField", "downPaymentMethodSection", "downPaymentDateField", "downPaymentInstallmentsInput",
    // Price preview display
    "firstMonthlySubtotalDisplay",
    // Product info panel targets
    "productInfoPanel", "funeraryInfoPanel", "cemeteryInfoPanel",
    "infoAdhesionAmount", "infoMonthlyAmount", "infoMaxAdhesionInstallments", "infoMaxDownPaymentInstallments", "infoIncludedBeneficiaries", "infoBeneficiaryPrice",
    "infoIncludedAgregados", "infoAgregadoPrice", "infoGraceBeneficiaries", "infoGraceAgregados",
    "infoMaintenanceFee", "infoFinancialGrace", "infoInterestRate", "infoInstallmentsNumber", "infoGraceDays",
    // Dynamic labels (change based on product type: funerary vs cemetery)
    "downPaymentMethodLabel", "downPaymentAmountLabel", "downPaymentInstallmentsLabel", "downPaymentDateLabel",
    // Isento option (funerary only)
    "isentoOption",
    // Payment method grid (to adjust columns based on product type)
    "paymentMethodGrid",
    // Fill discount button (Isentar 100%)
    "fillDiscountButton",
    // Cemetery discount field (simple, no Isentar 100% button)
    "cemeteryDiscountField",
    // Sticky bar total entry
    "stickyTotalEntry", "stickyTotalEntryValue",
    // Funerary auto-calculated entry display
    "funeraryEntryDisplayField", "funeraryEntryValue",
    // Funerary first installment (Valor da Entrada)
    "funeraryFirstInstallmentField", "funeraryFirstInstallmentValue",
    // Momento da cobrança (só produto com remoção)
    "chargeMomentSection",
    // Composição do pagamento (cemitério): preço do ERP, negociado ⇄ desconto, entrada, saldo
    "cascade", "referencePriceRow", "referencePrice", "referencePriceNote", "negotiatedField", "negotiatedInput",
    "entryError", "balanceRow", "balance", "cashBadge"
  ]
  static values = {
    productPrice: String,
    maintenanceFee: String,
    productType: String,
    pricingModel: String,
    portabilityDiscountLimit: Number,
    adhesionAmount: { type: Number, default: 0 },
    // Pricing rules for local calculation
    monthlyAmount: { type: Number, default: 0 },
    includedBeneficiaries: { type: Number, default: 0 },
    beneficiaryPrice: { type: Number, default: 0 },
    includedAgregados: { type: Number, default: 0 },
    agregadoPrice: { type: Number, default: 0 },
    // New funerary plan values
    maxAdhesionInstallments: { type: Number, default: 0 },
    maxDownPaymentInstallments: { type: Number, default: 0 },
    allowsBeneficiaries: { type: Boolean, default: true },
    // Seller pre-approved discount limit
    sellerDiscountLimitType: { type: String, default: "" },
    sellerDiscountLimitValue: { type: Number, default: 0 },
    // Preço do produto no Protheus: consultado ao escolher o produto, ou
    // trazido do servidor quando a venda já tem simulação para este produto.
    erpPriceUrl: String,
    erpPriceCents: { type: Number, default: 0 },
    erpPriceProductId: String
  }

  connect() {
    // Track previous payment method to detect isento -> other transitions
    this.previousPaymentMethod = null
    // Track current product ID to avoid clearing adicionals unnecessarily
    this.currentProductId = null
    // Initialize product data from selected product if exists
    this.initializeProductData()
    // Toggle payment section visibility based on product selection
    this.togglePaymentSection()
    // Check if any payment method is already selected (no scroll on page load)
    this.updateFields(false)
    // O editor de formas conecta DEPOIS deste controller, então ele perderia um
    // anúncio feito aqui. Ele pergunta quando estiver pronto, e a gente responde.
    this.targetRequestHandler = () => this.answerTargetRequest()
    document.addEventListener("payment-tenders:need-target", this.targetRequestHandler)
    this.modeAppliedHandler = () => this.updateFields(false)
    document.addEventListener("payment-tenders:mode-applied", this.modeAppliedHandler)
    // A soma das formas É a entrada no split, então ela reage exatamente como o
    // campo único reage ao ser digitado — as mesmas duas ações que estão no
    // `input` dele. Chamar só uma deixaria metade dos efeitos de fora.
    this.sumChangedHandler = () => {
      this.checkAVista()
      this.updateAdhesionFieldsVisibility()
    }
    document.addEventListener("payment-tenders:sum-changed", this.sumChangedHandler)
    // Check if it's à vista on load
    this.checkAVista()
    // Set up scroll listener for sticky bar
    this.setupScrollListener()
    // Listen for adicionals changes
    this.setupAdicionalsListener()
  }

  disconnect() {
    if (this.targetRequestHandler) {
      document.removeEventListener("payment-tenders:need-target", this.targetRequestHandler)
    }
    if (this.modeAppliedHandler) {
      document.removeEventListener("payment-tenders:mode-applied", this.modeAppliedHandler)
    }
    if (this.sumChangedHandler) {
      document.removeEventListener("payment-tenders:sum-changed", this.sumChangedHandler)
    }
    // Clean up scroll listener
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler)
    }
    // Clean up mutation observers
    if (this.beneficiariesObserver) {
      this.beneficiariesObserver.disconnect()
    }
    if (this.adicionalsObserver) {
      this.adicionalsObserver.disconnect()
    }
    // Clean up adicionals event listener
    if (this.adicionalsChangedHandler) {
      this.element.removeEventListener('adicionals:changed', this.adicionalsChangedHandler)
    }
  }

  setupAdicionalsListener() {
    // Listen for adicionals:changed event to recalculate values
    this.adicionalsChangedHandler = () => {
      this.updateAdhesionCalculation()
      this.calculatePricePreview()
    }
    this.element.addEventListener('adicionals:changed', this.adicionalsChangedHandler)
  }

  setupScrollListener() {
    this.scrollHandler = () => this.handleScroll()
    window.addEventListener('scroll', this.scrollHandler, { passive: true })
  }

  handleScroll() {
    if (!this.hasStickyProductSummaryTarget || !this.productSelected) return

    const scrollY = window.scrollY
    const threshold = 200 // Show after scrolling 200px

    if (scrollY > threshold) {
      this.showStickyBar()
    } else {
      this.hideStickyBar()
    }
  }

  showStickyBar() {
    if (!this.hasStickyProductSummaryTarget) return
    this.stickyProductSummaryTarget.classList.remove('hidden', '-translate-y-full')
    this.stickyProductSummaryTarget.classList.add('translate-y-0')
    // Add scroll padding to prevent content from being hidden behind the bar
    // Use actual bar height (52px) + small buffer for scroll-padding
    document.documentElement.style.scrollPaddingTop = '60px'
  }

  hideStickyBar() {
    if (!this.hasStickyProductSummaryTarget) return
    this.stickyProductSummaryTarget.classList.add('-translate-y-full')
    this.stickyProductSummaryTarget.classList.remove('translate-y-0')
    // Remove scroll padding when bar is hidden
    document.documentElement.style.scrollPaddingTop = '0px'
  }

  // Get the sticky bar height plus padding for comfortable viewing
  get stickyBarOffset() {
    // Sticky bar is approximately 52px
    // Add extra padding for breathing room:
    // - On mobile: need more space because elements are stacked
    // - On desktop: less space needed
    const isMobile = window.innerWidth < 1024 // lg breakpoint
    return isMobile ? 80 : 72
  }

  // Smooth scroll to an element with proper offset for sticky bar
  scrollToElement(element) {
    if (!element) return

    // Wait for any layout shifts to settle
    const elementRect = element.getBoundingClientRect()
    const absoluteElementTop = elementRect.top + window.scrollY
    const offsetPosition = absoluteElementTop - this.stickyBarOffset

    window.scrollTo({
      top: Math.max(0, offsetPosition),
      behavior: 'smooth'
    })
  }

  // Cobrar na porta só existe quando o produto gera uma etapa no local do
  // cliente. Sem remoção, a opção some e o valor volta pro padrão — senão a
  // venda entra num fluxo que espera motorista para um plano que não tem um.
  toggleChargeMoment(selectedProduct) {
    if (!this.hasChargeMomentSectionTarget) return

    const requiresRemoval = selectedProduct?.dataset.requiresRemoval === "true"
    this.chargeMomentSectionTarget.classList.toggle("hidden", !requiresRemoval)

    if (requiresRemoval) return

    const upfront = this.chargeMomentSectionTarget
      .querySelector('input[name="direct_sale[charge_moment]"][value="upfront"]')
    if (upfront && !upfront.checked) {
      upfront.checked = true
      upfront.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  initializeProductData() {
    const selectedProduct = this.element.querySelector('input[name="direct_sale[product_id]"]:checked')
    if (selectedProduct) {
      const productPrice = selectedProduct.dataset.productPrice
      const productMaintenanceFee = selectedProduct.dataset.productMaintenanceFee
      const productType = selectedProduct.dataset.productType
      const portabilityDiscountLimit = selectedProduct.dataset.portabilityDiscountLimit

      // Track the current product ID to detect actual product changes
      this.currentProductId = selectedProduct.value

      if (productPrice) this.productPriceValue = productPrice
      if (productMaintenanceFee) this.maintenanceFeeValue = productMaintenanceFee
      if (productType) this.productTypeValue = productType
      this.portabilityDiscountLimitValue = parseFloat(portabilityDiscountLimit) || 0

      this.toggleChargeMoment(selectedProduct)

      // Load pricing rules for funerary plans
      this.loadPricingRules(selectedProduct)
      this.fillMaintenanceFeeDefault()
      this.productChanged(selectedProduct, { initial: true })

      // Update beneficiaries limits from selected product
      this.updateBeneficiariesLimits(selectedProduct)

      // Update beneficiaries section visibility based on initial product type
      // Pass productChanged: true on initial load to populate adicionals grid
      this.toggleFuneraryPlanSections({ productChanged: true })

      // Show payment method section so user can select payment method
      // For funerary plans: only show after sale type is selected
      // For cemetery products: show immediately
      if (this.hasDownPaymentMethodSectionTarget) {
        if (productType === "funerary_plan") {
          // Check if sale type is selected
          const saleTypeSelected = this.isSaleTypeSelected()
          if (saleTypeSelected) {
            this.downPaymentMethodSectionTarget.classList.remove("hidden")
          } else {
            this.downPaymentMethodSectionTarget.classList.add("hidden")
          }
        } else {
          this.downPaymentMethodSectionTarget.classList.remove("hidden")
        }
      }

      // Update dynamic labels based on product type (funerary vs cemetery)
      this.updateDynamicLabels(productType)

      // Update sticky summary bar
      this.updateStickySummary(selectedProduct)

      // Update product info panel
      this.updateProductInfoPanel(selectedProduct)

      // Setup price preview listeners and calculate initial preview
      if (productType === "funerary_plan") {
        this.setupPricePreviewListeners()
        // Delay to ensure other controllers are initialized
        setTimeout(() => {
          this.calculatePricePreview()
          // Also update adhesion fields visibility for pre-filled discount values (after simulation)
          this.updateAdhesionFieldsVisibility()
        }, 100)
      }
    }
  }

  loadPricingRules(productElement) {
    this.pricingModelValue = productElement.dataset.pricingModel || ""
    this.monthlyAmountValue = parseFloat(productElement.dataset.monthlyAmount) || 0
    this.includedBeneficiariesValue = parseInt(productElement.dataset.includedBeneficiaries) || 0
    this.beneficiaryPriceValue = parseFloat(productElement.dataset.beneficiaryPrice) || 0
    this.includedAgregadosValue = parseInt(productElement.dataset.includedAgregados) || 0
    this.agregadoPriceValue = parseFloat(productElement.dataset.agregadoPrice) || 0
    // Store adhesion amount for portability discount calculation
    const adhesionStr = productElement.dataset.productAdhesionAmount || "0"
    this.adhesionAmountValue = this.parsePrice(adhesionStr)
    // New funerary plan values
    this.maxAdhesionInstallmentsValue = parseInt(productElement.dataset.maxAdhesionInstallments) || 0
    this.maxDownPaymentInstallmentsValue = parseInt(productElement.dataset.maxDownPaymentInstallments) || 0
    this.allowsBeneficiariesValue = productElement.dataset.allowsBeneficiaries !== "false"
    // Seller pre-approved discount limit
    this.sellerDiscountLimitTypeValue = productElement.dataset.sellerDiscountLimitType || ""
    this.sellerDiscountLimitValueValue = parseFloat(productElement.dataset.sellerDiscountLimitValue) || 0
  }

  setupPricePreviewListeners() {
    // Prevent duplicate listener setup
    if (this._pricePreviewListenersSetup) return
    this._pricePreviewListenersSetup = true

    // Listen for changes in discount field
    const discountField = this.element.querySelector('input[name="direct_sale[discount_amount]"]')
    if (discountField) {
      this._discountInputHandler = () => {
        this.calculatePricePreview()
        this.updateAdhesionCalculation()
        this.updateAdhesionFieldsVisibility()
        this.toggleFuneraryPlanSections()
      }
      discountField.addEventListener("input", this._discountInputHandler)
      discountField.addEventListener("blur", this._discountInputHandler)
    }

    // Listen for changes in beneficiaries section using MutationObserver
    if (this.hasBeneficiariesSectionTarget && !this.beneficiariesObserver) {
      this.beneficiariesObserver = new MutationObserver(() => {
        this.calculatePricePreview()
      })
      this.beneficiariesObserver.observe(this.beneficiariesSectionTarget, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class"]
      })
    }

    // Listen for changes in adicionals section
    if (this.hasAdicionaisSectionTarget && !this.adicionalsObserver) {
      this.adicionalsObserver = new MutationObserver(() => {
        this.calculatePricePreview()
      })
      this.adicionalsObserver.observe(this.adicionaisSectionTarget, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class", "value"]
      })

      // Also listen for input events on quantity fields
      this._adicionalsInputHandler = (e) => {
        if (e.target.name && e.target.name.includes("quantity")) {
          this.calculatePricePreview()
        }
      }
      this.adicionaisSectionTarget.addEventListener("input", this._adicionalsInputHandler)
    }
  }

  updateBeneficiariesLimits(productElement) {
    if (!this.hasBeneficiariesSectionTarget) return

    const maxBeneficiaries = parseInt(productElement.dataset.maxBeneficiaries) || 10
    const maxAgregados = parseInt(productElement.dataset.maxAgregados) || 5
    const includedBeneficiaries = parseInt(productElement.dataset.includedBeneficiaries) || 0
    const includedAgregados = parseInt(productElement.dataset.includedAgregados) || 0
    const beneficiaryPrice = parseFloat(productElement.dataset.beneficiaryPrice) || 0
    const agregadoPrice = parseFloat(productElement.dataset.agregadoPrice) || 0

    // Try the new beneficiaries-quantity controller first
    const quantityElement = this.beneficiariesSectionTarget.querySelector("[data-controller='beneficiaries-quantity']")
    if (quantityElement) {
      const quantityController = this.application.getControllerForElementAndIdentifier(
        quantityElement,
        "beneficiaries-quantity"
      )
      if (quantityController) {
        quantityController.maxBeneficiariesValue = maxBeneficiaries
        quantityController.maxAgregadosValue = maxAgregados
        quantityController.includedBeneficiariesValue = includedBeneficiaries
        quantityController.includedAgregadosValue = includedAgregados
        quantityController.beneficiaryPriceValue = beneficiaryPrice
        quantityController.agregadoPriceValue = agregadoPrice
        // Update UI with new values
        quantityController.updateUI()
        return
      }
    }

    // Fallback: try the old beneficiaries controller
    const beneficiariesElement = this.beneficiariesSectionTarget.querySelector("[data-controller='beneficiaries']")
    if (!beneficiariesElement) return

    const beneficiariesController = this.application.getControllerForElementAndIdentifier(
      beneficiariesElement,
      "beneficiaries"
    )
    if (beneficiariesController) {
      beneficiariesController.maxBeneficiariesValue = maxBeneficiaries
      beneficiariesController.maxAgregadosValue = maxAgregados
    }
  }

  updateProductData(event) {
    const productPrice = event.target.dataset.productPrice
    const productMaintenanceFee = event.target.dataset.productMaintenanceFee
    const productType = event.target.dataset.productType
    const portabilityDiscountLimit = event.target.dataset.portabilityDiscountLimit
    const newProductId = event.target.value

    // Check if product actually changed (to avoid clearing adicionals unnecessarily)
    const productChanged = this.currentProductId !== newProductId
    this.currentProductId = newProductId

    this.toggleChargeMoment(event.target)

    if (productPrice) this.productPriceValue = productPrice
    if (productMaintenanceFee) this.maintenanceFeeValue = productMaintenanceFee
    if (productType) this.productTypeValue = productType
    this.portabilityDiscountLimitValue = parseFloat(portabilityDiscountLimit) || 0

    // Load pricing rules for funerary plans
    this.loadPricingRules(event.target)
    this.productChanged(event.target, { initial: false })

    // Show payment section when product is selected
    this.togglePaymentSection()

    // Show payment method section so user can select payment method
    // For funerary plans: only show after sale type is selected
    // For cemetery products: show immediately
    if (this.hasDownPaymentMethodSectionTarget) {
      if (productType === "funerary_plan") {
        // Check if sale type is selected
        const saleTypeSelected = this.isSaleTypeSelected()
        if (saleTypeSelected) {
          this.downPaymentMethodSectionTarget.classList.remove("hidden")
        } else {
          this.downPaymentMethodSectionTarget.classList.add("hidden")
        }
      } else {
        this.downPaymentMethodSectionTarget.classList.remove("hidden")
      }
    }

    // Show/hide funerary plan sections based on product type
    // Only update adicionals when product actually changed
    this.toggleFuneraryPlanSections({ productChanged })

    // Update dynamic labels based on product type (funerary vs cemetery)
    this.updateDynamicLabels(productType)

    // Update beneficiaries limits from selected product
    this.updateBeneficiariesLimits(event.target)

    // Update sticky summary bar
    this.updateStickySummary(event.target)

    // Update product info panel
    this.updateProductInfoPanel(event.target)

    // Setup price preview listeners for funerary plans
    if (productType === "funerary_plan") {
      this.setupPricePreviewListeners()
      // Calculate initial price preview after a delay
      setTimeout(() => this.calculatePricePreview(), 200)
      // Update discount hint based on sale type and new product
      setTimeout(() => this.updateDiscountHint(), 100)
    } else {
      // Update discount hint for cemetery products too (seller limits apply)
      setTimeout(() => this.updateDiscountHint(), 100)
      // Hide price preview for non-funerary products
      if (this.hasPricePreviewSectionTarget) {
        this.pricePreviewSectionTarget.classList.add("hidden")
      }
      // Hide payment preview panel for non-funerary products
      if (this.hasPaymentPreviewPanelTarget) {
        this.paymentPreviewPanelTarget.classList.add("hidden")
      }
    }

    // Smooth scroll after DOM update - always scroll to product details section
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (this.hasProductInfoPanelTarget && !this.productInfoPanelTarget.classList.contains('hidden')) {
          this.scrollToElement(this.productInfoPanelTarget)
        } else if (this.hasPaymentSectionTarget) {
          this.scrollToElement(this.paymentSectionTarget)
        }
      }, 250)
    })
  }

  toggleFuneraryPlanSections(options = {}) {
    const { productChanged = false } = options
    const isFuneraryPlan = this.productTypeValue === "funerary_plan"
    const hasPaymentMethod = this.getSelectedPaymentMethod() !== null
    const isFullyDiscounted = this.isAdhesionFullyDiscounted()

    // Show/hide beneficiaries section
    // For funerary plans: show IMMEDIATELY when product is selected
    // AND only if the product allows beneficiaries (e.g., Jatobá Único does not)
    if (this.hasBeneficiariesSectionTarget) {
      if (isFuneraryPlan && this.allowsBeneficiariesValue) {
        this.beneficiariesSectionTarget.classList.remove("hidden")
      } else {
        this.beneficiariesSectionTarget.classList.add("hidden")
      }
    }

    // Show/hide adicionals section
    // For funerary plans: show IMMEDIATELY when product is selected
    // Only update available adicionals when product actually changes (to preserve user selections)
    if (this.hasAdicionaisSectionTarget) {
      if (isFuneraryPlan) {
        this.adicionaisSectionTarget.classList.remove("hidden")
        // Only update available adicionals when product actually changed
        if (productChanged) {
          this.updateAdicionalsFromProduct()
        }
      } else {
        this.adicionaisSectionTarget.classList.add("hidden")
      }
    }

    // Show/hide adhesion installments field for funerary plans
    // Só adesão não tem parcelamento contratual separado do recebimento.
    if (this.hasAdhesionInstallmentsFieldTarget) {
      if (isFuneraryPlan && this.pricingModelValue !== "adhesion_only" && hasPaymentMethod && !isFullyDiscounted) {
        this.adhesionInstallmentsFieldTarget.classList.remove("hidden")
        // Set min to 1 (user can pay in a single installment)
        if (this.hasAdhesionInstallmentsInputTarget) {
          this.adhesionInstallmentsInputTarget.setAttribute("min", 1)
        }
        // Update max installments hint
        if (this.hasAdhesionInstallmentsMaxTarget && this.maxAdhesionInstallmentsValue > 0) {
          this.adhesionInstallmentsMaxTarget.textContent = `(máx: ${this.maxAdhesionInstallmentsValue})`
        }
        // Set max attribute on input
        if (this.hasAdhesionInstallmentsInputTarget && this.maxAdhesionInstallmentsValue > 0) {
          this.adhesionInstallmentsInputTarget.setAttribute("max", this.maxAdhesionInstallmentsValue)
        }
        // Calculate initial adhesion installment value
        this.updateAdhesionCalculation()
      } else {
        this.adhesionInstallmentsFieldTarget.classList.add("hidden")
        // Reset to 1 when hidden
        if (this.hasAdhesionInstallmentsInputTarget) {
          this.adhesionInstallmentsInputTarget.value = 1
          this.adhesionInstallmentsInputTarget.setAttribute("min", 1)
        }
      }
    }

    // Show/hide contract first due date field for funerary plans
    // Show when payment method is selected OR when fully discounted (mensalidade still applies)
    if (this.hasContractFirstDueDateFieldTarget) {
      if (isFuneraryPlan && (hasPaymentMethod || isFullyDiscounted)) {
        this.contractFirstDueDateFieldTarget.classList.remove("hidden")
      } else {
        this.contractFirstDueDateFieldTarget.classList.add("hidden")
      }
    }

    // Hide/show payment fields based on product type
    // Funerary plans don't have: interest type, funding installments, maintenance tax
    // But they DO have: first mensalidade date (repurposed from first funding date)
    if (isFuneraryPlan) {
      // Hide fields not applicable for funerary plans
      if (this.hasInterestTypeTarget) {
        this.interestTypeTarget.classList.add("hidden")
        if (this.hasInterestTypeSelectTarget) {
          this.interestTypeSelectTarget.removeAttribute("required")
        }
      }

      if (this.hasFundingInstallmentsTarget) {
        this.fundingInstallmentsTarget.classList.add("hidden")
        // Set funding installments to 1 for funerary plans (API expects this)
        if (this.hasFundingInstallmentsInputTarget) {
          this.fundingInstallmentsInputTarget.value = 1
        }
      }

      if (this.hasMaintenanceTaxFieldTarget) {
        this.maintenanceTaxFieldTarget.classList.add("hidden")
        if (this.hasMaintenanceTaxTarget) {
          this.maintenanceTaxTarget.removeAttribute("required")
        }
      }

      // Hide first funding date for funerary plans (calculated automatically by API)
      if (this.hasFirstFundingDateTarget) {
        this.firstFundingDateTarget.classList.add("hidden")
      }
    } else {
      // For cemetery products, show maintenance tax and let checkAVista handle the rest
      const selectedMethod = this.getSelectedPaymentMethod()
      if (selectedMethod) {
        if (this.hasMaintenanceTaxFieldTarget) {
          this.maintenanceTaxFieldTarget.classList.remove("hidden")
        }
        // Interest type, funding installments, first funding date are handled by checkAVista
        // based on whether it's à vista or not
        this.checkAVista()
      }
    }

    // Labels stay the same for funerary and cemetery:
    // - "Valor da Entrada" (down payment amount)
    // - "Data da Entrada" (down payment date)

    if (this.hasFillPriceButtonTextTarget) {
      this.fillPriceButtonTextTarget.textContent = isFuneraryPlan
        ? "Preencher Adesão"
        : "À Vista"
    }


    // For funerary plans: show these fields immediately when product is selected
    // (before payment method selection)
    if (this.hasSaleTypeSectionTarget) {
      if (isFuneraryPlan) {
        this.saleTypeSectionTarget.classList.remove("hidden")
      } else {
        this.saleTypeSectionTarget.classList.add("hidden")
      }
    }

    // Valor da Entrada - show appropriate field based on product type
    // Cemetery: manual input field
    // Funerary: auto-calculated display fields (Adesão Final + Valor da Entrada)
    if (this.hasDownPaymentAmountFieldTarget && this.hasFuneraryEntryDisplayFieldTarget) {
      if (isFuneraryPlan) {
        // Hide manual input for funerary, show auto-calculated displays
        this.downPaymentAmountFieldTarget.classList.add("hidden")
        if (hasPaymentMethod && !isFullyDiscounted) {
          this.funeraryEntryDisplayFieldTarget.classList.remove("hidden")
          // Show first installment field too
          if (this.hasFuneraryFirstInstallmentFieldTarget) {
            this.funeraryFirstInstallmentFieldTarget.classList.remove("hidden")
          }
          // Calculate and update the entry values
          this.updateFuneraryEntryValue()
        } else {
          this.funeraryEntryDisplayFieldTarget.classList.add("hidden")
          if (this.hasFuneraryFirstInstallmentFieldTarget) {
            this.funeraryFirstInstallmentFieldTarget.classList.add("hidden")
          }
        }
      } else {
        // Show manual input for cemetery, hide funerary displays
        this.funeraryEntryDisplayFieldTarget.classList.add("hidden")
        if (this.hasFuneraryFirstInstallmentFieldTarget) {
          this.funeraryFirstInstallmentFieldTarget.classList.add("hidden")
        }
        if (hasPaymentMethod) {
          this.downPaymentAmountFieldTarget.classList.remove("hidden")
        } else {
          this.downPaymentAmountFieldTarget.classList.add("hidden")
        }
      }
    } else if (this.hasDownPaymentAmountFieldTarget) {
      // Fallback if funerary display target doesn't exist
      if (hasPaymentMethod && !isFuneraryPlan) {
        this.downPaymentAmountFieldTarget.classList.remove("hidden")
      } else {
        this.downPaymentAmountFieldTarget.classList.add("hidden")
      }
    }

    // Desconto - show for both funerary and cemetery when payment method is selected
    if (this.hasDiscountFieldTarget) {
      if (hasPaymentMethod) {
        this.discountFieldTarget.classList.remove("hidden")
      } else {
        this.discountFieldTarget.classList.add("hidden")
      }
    }

    // O desconto é no produto, não na entrada: o nome antigo fazia parecer
    // que ele abatia a entrada.
    if (this.hasDiscountLabelTarget) {
      this.discountLabelTarget.textContent = isFuneraryPlan
        ? "Desconto da Adesão"
        : "Desconto no produto"
    }

    // Show/hide "Isentar 100%" button (funerary only)
    if (this.hasFillDiscountButtonTarget) {
      if (isFuneraryPlan) {
        this.fillDiscountButtonTarget.classList.remove("hidden")
        this.fillDiscountButtonTarget.classList.add("inline-flex")
      } else {
        this.fillDiscountButtonTarget.classList.add("hidden")
        this.fillDiscountButtonTarget.classList.remove("inline-flex")
      }
    }

    // Update discount hint based on current sale type and seller limits
    this.updateDiscountHint()
  }

  updateAdicionalsFromProduct() {
    // Get the adicionals controller
    const adicionalsElement = this.adicionaisSectionTarget.querySelector("[data-controller='adicionals']")
    if (!adicionalsElement) return

    const adicionalsController = this.application.getControllerForElementAndIdentifier(
      adicionalsElement,
      "adicionals"
    )
    if (!adicionalsController) return

    // Get adicionals data from selected product
    const selectedProduct = this.element.querySelector('input[name="direct_sale[product_id]"]:checked')
    if (!selectedProduct) return

    const adicionalsJson = selectedProduct.dataset.productAdicionals
    if (!adicionalsJson) return

    try {
      const adicionals = JSON.parse(adicionalsJson)
      adicionalsController.updateAvailableAdicionals(adicionals)
    } catch (e) {
      console.error("Error parsing adicionals JSON:", e)
    }
  }

  // O checkbox do misto não deve rolar a tela: quem rola é a escolha da forma
  // de pagamento, onde o vendedor precisa ver os campos que apareceram. O
  // Stimulus passa o evento como 1º argumento, e objeto é truthy — por isso
  // qualquer ação ligada direto em updateFields rolava.
  updateFieldsWithoutScroll() {
    this.updateFields(false)
  }

  updateFields(shouldScroll = true) {
    const selectedMethod = this.getSelectedPaymentMethod()
    const isFuneraryPlan = this.productTypeValue === "funerary_plan"

    // Detect when switching from "isento" to another method and clear the auto-filled values
    if (isFuneraryPlan && this.previousPaymentMethod === 'isento' && selectedMethod && selectedMethod !== 'isento') {
      this.clearIsentoValues()
    }

    // Track current method for next change detection
    if (selectedMethod) {
      this.previousPaymentMethod = selectedMethod
    }

    if (selectedMethod) {
      // Show conditional fields based on product type
      // DON'T show cemetery-only fields for funerary products
      this.conditionalFieldTargets.forEach(field => {
        // Skip cemetery-only fields for funerary plans
        if (isFuneraryPlan) {
          const isCemeteryOnly = field === this.interestTypeTarget ||
                                  field === this.fundingInstallmentsTarget ||
                                  field === this.firstFundingDateTarget ||
                                  field === this.maintenanceTaxFieldTarget ||
                                  field === this.downPaymentAmountFieldTarget ||
                                  (field.dataset.paymentFieldsTarget || "").split(" ").includes("cemeteryOnlyField")
          if (isCemeteryOnly) {
            field.classList.add('hidden')
            return
          }
        }
        field.classList.remove('hidden')
      })

      // Add required attribute to interest type when shown (cemetery only)
      if (this.hasInterestTypeSelectTarget && !isFuneraryPlan) {
        this.interestTypeSelectTarget.setAttribute('required', 'required')
      }

      // Handle "isento" payment method for funerary plans
      if (selectedMethod === 'isento' && this.productTypeValue === "funerary_plan") {
        // Hide down payment installments (no installments for exempt)
        if (this.hasDownPaymentInstallmentsTarget) {
          this.downPaymentInstallmentsTarget.classList.add('hidden')
        }
        // Show but DISABLE down payment amount and discount fields (auto-filled, read-only)
        if (this.hasDownPaymentAmountFieldTarget) {
          this.downPaymentAmountFieldTarget.classList.remove('hidden')
        }
        if (this.hasDiscountFieldTarget) {
          this.discountFieldTarget.classList.remove('hidden')
        }
        // Disable the inputs
        this.setIsentoFieldsDisabled(true)
        // Hide adhesion installments (no installments when exempt)
        if (this.hasAdhesionInstallmentsFieldTarget) {
          this.adhesionInstallmentsFieldTarget.classList.add('hidden')
        }
      } else if (selectedMethod === 'pix' || selectedMethod === 'cash' || selectedMethod === 'misto') {
        // Esconde o parcelamento ÚNICO da entrada — pix/cash são à vista e o
        // misto parcela por forma (o cartão de cada forma tem suas parcelas).
        // Esconder não apaga: o 3x do cartão ia no submit e o servidor recusava
        // "não se aplica a PIX" num campo que o vendedor nem via.
        if (this.hasDownPaymentInstallmentsTarget) {
          this.downPaymentInstallmentsTarget.classList.add('hidden')
        }
        if (this.hasDownPaymentInstallmentsInputTarget) {
          this.downPaymentInstallmentsInputTarget.value = 1
        }
        // Enable fields that might have been disabled by isento
        this.setIsentoFieldsDisabled(false)
      } else {
        // Show installments for POS and credit card
        if (this.hasDownPaymentInstallmentsTarget) {
          this.downPaymentInstallmentsTarget.classList.remove('hidden')
        }
        // Enable fields that might have been disabled by isento
        this.setIsentoFieldsDisabled(false)
      }

      // Apply product-type-specific field visibility (funerary vs cemetery)
      this.toggleFuneraryPlanSections()

      // Com o split (misto) ligado, a seção de forma fica escondida (o editor de
      // split a substitui) — garante isso depois de qualquer updateFields, mesmo
      // que "misto" tenha sido tratado como forma selecionada acima.
      if (this.mistoEnabled()) {
        if (this.hasDownPaymentMethodSectionTarget) {
          this.downPaymentMethodSectionTarget.classList.add('hidden')
        }
        // A soma das formas vira o valor da entrada — o campo único de valor
        // não faz sentido com o split ligado.
        if (this.hasDownPaymentAmountFieldTarget) {
          this.downPaymentAmountFieldTarget.classList.add('hidden')
        }
      }

      // Recalculate price preview for funerary plans when payment method changes
      if (this.productTypeValue === "funerary_plan") {
        this.setupPricePreviewListeners()
        setTimeout(() => this.calculatePricePreview(), 100)
      }

      // Smooth scroll to the first visible conditional field (only on user interaction)
      if (shouldScroll) {
        // Use requestAnimationFrame to ensure DOM has updated before scrolling
        requestAnimationFrame(() => {
          setTimeout(() => {
            const firstVisibleField = this.conditionalFieldTargets.find(field => !field.classList.contains('hidden'))
            if (firstVisibleField) {
              this.scrollToElement(firstVisibleField)
            }
          }, 150)
        })
      }
    } else {
      // Hide all conditional fields if no payment method is selected
      // BUT keep payment method section visible so user can select one
      // AND for funerary plans, keep some essential fields visible
      const isFuneraryPlan = this.productTypeValue === "funerary_plan"
      const hasProductSelected = this.productTypeValue !== ""
      const hasSaleTypeSelected = this.isSaleTypeSelected()

      this.conditionalFieldTargets.forEach(field => {
        // For funerary plans: show payment method section only after sale type is selected
        // For cemetery products: show payment method section when product is selected
        if (hasProductSelected && field === this.downPaymentMethodSectionTarget) {
          if (isFuneraryPlan) {
            if (hasSaleTypeSelected) {
              field.classList.remove('hidden')
            } else {
              field.classList.add('hidden')
            }
          } else {
            field.classList.remove('hidden')
          }
          return // Skip further processing for this field
        }

        // For funerary plans, show sale type section (Venda, Portabilidade, Reativação)
        if (isFuneraryPlan && field === this.saleTypeSectionTarget) {
          field.classList.remove('hidden')
          return // Skip hiding this field
        }

        // For funerary plans, show beneficiaries section immediately
        if (isFuneraryPlan && this.hasBeneficiariesSectionTarget && field === this.beneficiariesSectionTarget) {
          if (this.allowsBeneficiariesValue) {
            field.classList.remove('hidden')
          }
          return // Skip further processing for this field
        }

        // For funerary plans, show adicionals section immediately
        if (isFuneraryPlan && this.hasAdicionaisSectionTarget && field === this.adicionaisSectionTarget) {
          field.classList.remove('hidden')
          return // Skip further processing for this field
        }

        if (hasProductSelected && this.cascadeFieldFor(field)) return

        field.classList.add('hidden')
      })

      // Remove required attribute from interest type when hidden
      if (this.hasInterestTypeSelectTarget) {
        this.interestTypeSelectTarget.removeAttribute('required')
      }

      // For funerary plans, still show/hide other sections based on discount state
      if (isFuneraryPlan) {
        this.toggleFuneraryPlanSections()
      }
    }

    // Fora do if/else de propósito: sem forma de pagamento o ramo de baixo não
    // chamava ninguém que decidisse a data, e ela ficava na tela sozinha depois
    // de desmarcar o split. Última linha do método, para nada desfazer.
    this.syncDownPaymentDateVisibility()
    // Os condicionais reexibem o trio de financiamento; a cascata decide de novo
    // (também ao ligar/desligar as formas, que não escolhe forma nenhuma).
    if (this.productTypeValue !== "" && this.productTypeValue !== "funerary_plan") this.recomputeCascade()
  }

  // Mantido pelo nome: quem chamava "checar à vista" hoje recalcula a cascata.
  checkAVista() {
    if (this.productTypeValue === "funerary_plan") return
    if (!this.getSelectedPaymentMethod()) return

    this.recomputeCascade()
  }

  // Converte "R$ 1.190,00" / "1190,00" / "1.190" / "1190" em integer de centavos.
  // Formato brasileiro, sempre: ponto é milhar, vírgula é decimal. A máscara
  // escreve "20.000" enquanto o vendedor digita vinte mil, e ler isso como
  // vinte reais virava desconto de R$ 39.970,00 no valor negociado.
  _parseAmountToCents(rawValue) {
    if (rawValue === null || rawValue === undefined) return 0

    const cleaned = String(rawValue)
      .replace(/R\$\s*/i, '')
      .replace(/\s/g, '')
      .replace(/\./g, '')
      .replace(',', '.')

    const asNumber = parseFloat(cleaned)
    if (Number.isNaN(asNumber)) return 0
    return Math.round(asNumber * 100)
  }

  // ---- Composição do pagamento (cemitério) -----------------------------------
  //
  // Uma conta em cascata, e não campos soltos:
  //
  //   Preço no Protheus         (consultado ao escolher o produto)
  //   − Desconto no produto  ⇄  = Valor negociado   (um número, dois campos)
  //   − Entrada (paga agora)                        (máximo: o valor negociado)
  //   = Saldo a financiar                           (> 0 abre o financiamento)
  //
  // A tela nunca compara com o preço de cadastro: ele é o preço do site, e
  // comparar com ele mostrava financiamento em venda à vista e travava o
  // "prosseguir" no teto do ERP. A entrada acompanha o valor negociado só
  // enquanto for igual a ele (à vista); uma entrada digitada menor nunca é
  // mexida — mudar o desconto ali muda o saldo, não a entrada.

  productChanged(productEl, { initial }) {
    this.catalogCents = this._parseAmountToCents(productEl.dataset.productPrice)
    const cemetery = this.productTypeValue !== "funerary_plan"
    this.toggleCascadeRows(cemetery)
    if (!cemetery) return

    if (!initial) {
      // Taxa do produto novo, a menos que o vendedor tenha digitado a dele:
      // a do produto anterior ficava e a venda pedia aprovação sem motivo.
      if (!this.maintenanceDirty) this.applyMaintenanceFee()
    }
    this.loadReferencePrice(productEl.value)
  }

  // No cemitério a composição do pagamento aparece com o produto, antes da
  // forma: o dinheiro vem primeiro, a forma é de onde ele sai. No funerário
  // o desconto continua aparecendo só com a forma escolhida.
  toggleCascadeRows(show) {
    ;["referencePriceRow", "negotiatedField", "balanceRow", "discountField", "downPaymentAmountField"].forEach((name) => {
      const upper = name.charAt(0).toUpperCase() + name.slice(1)
      if (this[`has${upper}Target`]) this[`${name}Target`].classList.toggle("hidden", !show)
    })
  }

  // Campos da cascata que não dependem da forma escolhida (cemitério).
  cascadeFieldFor(field) {
    return this.productTypeValue !== "funerary_plan" &&
      ((this.hasDiscountFieldTarget && field === this.discountFieldTarget) ||
       (this.hasDownPaymentAmountFieldTarget && field === this.downPaymentAmountFieldTarget))
  }

  loadReferencePrice(productId) {
    const token = (this._priceToken = (this._priceToken || 0) + 1)

    if (this.erpPriceCentsValue > 0 && String(this.erpPriceProductIdValue) === String(productId)) {
      this.setReferencePrice(this.erpPriceCentsValue, { source: "erp" })
      return
    }
    if (!this.hasErpPriceUrlValue || !this.erpPriceUrlValue) {
      this.setReferencePrice(this.catalogCents, { source: "catalog", error: "Consulta ao Protheus indisponível" })
      return
    }

    this.referenceState = "loading"
    this.renderReferencePrice()

    fetch(`${this.erpPriceUrlValue}?product_id=${encodeURIComponent(productId)}`, {
      headers: { Accept: "application/json" },
      credentials: "same-origin"
    })
      .then((response) => response.json().then((body) => ({ ok: response.ok, body })))
      .then(({ ok, body }) => {
        if (token !== this._priceToken) return
        if (ok && Number(body.cents) > 0) {
          this.setReferencePrice(Number(body.cents), { source: "erp" })
        } else {
          this.setReferencePrice(this.catalogCents, { source: "catalog", error: body.error || "Protheus indisponível" })
        }
      })
      .catch(() => {
        if (token !== this._priceToken) return
        this.setReferencePrice(this.catalogCents, { source: "catalog", error: "Protheus indisponível" })
      })
  }

  setReferencePrice(cents, { source, error = null }) {
    this._referenceCents = cents
    this.referenceSource = source
    this.referenceError = error
    this.referenceState = "ready"
    this.renderReferencePrice()

    if (this.hasStickyProductPriceTarget && this.productTypeValue !== "funerary_plan") {
      this.stickyProductPriceTarget.textContent = this.formatCurrency(cents / 100)
      if (this.hasStickyProductPriceLabelTarget) {
        this.stickyProductPriceLabelTarget.textContent = source === "erp" ? "Preço Protheus:" : "Preço:"
      }
    }

    if (this.entryFollows === undefined) this.decideInitialFollow()
    this.recomputeCascade()
    this.updateDiscountHint()
  }

  renderReferencePrice() {
    if (!this.hasReferencePriceTarget) return

    if (this.referenceState === "loading") {
      this.referencePriceTarget.textContent = "consultando…"
      this.referencePriceTarget.classList.add("text-gray-400")
      if (this.hasReferencePriceNoteTarget) this.referencePriceNoteTarget.classList.add("hidden")
      return
    }

    this.referencePriceTarget.textContent = this.formatCurrency(this.referenceCents() / 100)
    this.referencePriceTarget.classList.remove("text-gray-400")
    if (!this.hasReferencePriceNoteTarget) return

    // A nota só existe quando o preço NÃO veio do Protheus.
    const fallback = this.referenceSource !== "erp"
    this.referencePriceNoteTarget.classList.toggle("hidden", !fallback)
    this.referencePriceNoteTarget.classList.toggle("text-amber-700", fallback)
    this.referencePriceNoteTarget.textContent = fallback
      ? `${this.referenceError || "Protheus indisponível"}: usando o preço de cadastro. Simule para consultar de novo.`
      : ""
  }

  // Na abertura, a entrada que veio do servidor é à vista quando está vazia,
  // quando é o default (preço de cadastro) ou quando já é o valor negociado.
  decideInitialFollow() {
    const entry = this.currentEntryCents()
    const negotiated = this.negotiatedCents()
    this.entryFollows = entry === 0 ||
      Math.abs(entry - negotiated) <= 1 ||
      Math.abs(entry - (this.catalogCents || 0)) <= 1
  }

  referenceCents() {
    return this._referenceCents || this.catalogCents || 0
  }

  discountCents() {
    const campo = this.element.querySelector('input[name="direct_sale[discount_amount]"]')
    return campo ? this._parseAmountToCents(campo.value) : 0
  }

  negotiatedCents() {
    return Math.max(this.referenceCents() - this.discountCents(), 0)
  }

  // Quanto de entrada a venda tem hoje: a soma das formas no split, o campo
  // único fora dele.
  currentEntryCents() {
    const somaSplit = this.element.querySelector(
      'input[type="hidden"][name="direct_sale[down_payment_amount]"]:not([disabled])'
    )
    if (somaSplit) return this._parseAmountToCents(somaSplit.value)
    if (!this.hasDownPaymentAmountTarget) return 0

    return this._parseAmountToCents(this.downPaymentAmountTarget.value)
  }

  discountChanged() {
    if (this._syncing) return
    this.recomputeCascade({ from: "discount" })
  }

  negotiatedChanged() {
    if (this._syncing || !this.hasNegotiatedInputTarget) return
    // Campo limpo no meio da digitação não é "desconto de 100%": espera o blur.
    if (this.negotiatedInputTarget.value.trim() === "") return

    const typed = this._parseAmountToCents(this.negotiatedInputTarget.value)
    const discount = Math.min(Math.max(this.referenceCents() - typed, 0), this.referenceCents())
    const campo = this.element.querySelector('input[name="direct_sale[discount_amount]"]')
    if (campo) this.writeMoney(campo, discount)
    this.recomputeCascade({ from: "negotiated" })
  }

  // Ao sair do campo: vazio volta ao preço cheio (sem desconto); acima do preço
  // volta ao preço; o número volta ao formato.
  negotiatedBlur() {
    if (this.hasNegotiatedInputTarget && this.negotiatedInputTarget.value.trim() === "") {
      const campo = this.element.querySelector('input[name="direct_sale[discount_amount]"]')
      if (campo) this.writeMoney(campo, 0)
    }
    this.recomputeCascade()
  }

  entryChanged() {
    if (this._syncing) return
    this.entryFollows = Math.abs(this.currentEntryCents() - this.negotiatedCents()) <= 1
    this.recomputeCascade({ from: "entry" })
  }

  // Entrada apagada e abandonada volta a ser à vista: é o que o vendedor quer
  // dizer ao limpar o campo, e o servidor não aceita entrada zero.
  entryBlur() {
    if (this.currentEntryCents() === 0 && !this.mistoEnabled()) {
      this.entryFollows = true
      this.recomputeCascade()
      return
    }
    this.entryChanged()
  }

  maintenanceTyped() {
    if (this._syncing) return
    this.maintenanceDirty = true
  }

  recomputeCascade({ from = null } = {}) {
    if (this.productTypeValue === "funerary_plan") return

    const negotiated = this.negotiatedCents()
    if (from !== "negotiated" && this.hasNegotiatedInputTarget) {
      this.writeMoney(this.negotiatedInputTarget, negotiated)
    }

    const misto = this.mistoEnabled()
    if (this.entryFollows && !misto && from !== "entry" && this.hasDownPaymentAmountTarget) {
      this.writeMoney(this.downPaymentAmountTarget, negotiated)
    }

    const entry = this.currentEntryCents()
    const balance = negotiated - entry
    const exceeds = entry > negotiated + 1
    const financed = balance > 1

    if (this.hasDownPaymentAmountTarget) {
      this.downPaymentAmountTarget.setCustomValidity(
        exceeds && !misto ? `A entrada não pode passar do valor negociado (${this.formatCurrency(negotiated / 100)}).` : ""
      )
    }
    if (this.hasEntryErrorTarget) {
      this.entryErrorTarget.textContent = exceeds
        ? `Acima do valor negociado (${this.formatCurrency(negotiated / 100)}): com esse valor a venda já está paga.`
        : ""
      this.entryErrorTarget.classList.toggle("hidden", !exceeds)
    }
    this.toggleFinancingFields(financed)

    if (this.hasBalanceTarget) {
      this.balanceTarget.textContent = this.formatCurrency(Math.max(balance, 0) / 100)
    }
    if (this.hasCashBadgeTarget) {
      const badge = this.cashBadgeTarget
      badge.textContent = exceeds ? "Acima do valor" : (financed ? "Entrada + financiamento" : "À vista")
      badge.classList.toggle("bg-green-100", !financed && !exceeds)
      badge.classList.toggle("text-green-800", !financed && !exceeds)
      badge.classList.toggle("bg-blue-100", financed && !exceeds)
      badge.classList.toggle("text-blue-800", financed && !exceeds)
      badge.classList.toggle("bg-red-100", exceeds)
      badge.classList.toggle("text-red-800", exceeds)
    }

    this.broadcastNegotiated()
  }

  // Tipo de financiamento, parcelas e 1º vencimento só existem com saldo. Sem
  // saldo o campo oculto de parcelas leva o sentinela 1 (a validação do
  // servidor exige > 0; o saldo é quem diz que é à vista).
  toggleFinancingFields(show) {
    if (this.hasInterestTypeTarget) {
      this.interestTypeTarget.classList.toggle("hidden", !show)
      if (this.hasInterestTypeSelectTarget) {
        if (show) this.interestTypeSelectTarget.setAttribute("required", "required")
        else this.interestTypeSelectTarget.removeAttribute("required")
      }
    }
    if (this.hasFundingInstallmentsTarget) this.fundingInstallmentsTarget.classList.toggle("hidden", !show)
    if (this.hasFirstFundingDateTarget) this.firstFundingDateTarget.classList.toggle("hidden", !show)
    if (!show && this.hasFundingInstallmentsInputTarget) this.fundingInstallmentsInputTarget.value = 1
  }

  // O editor de formas precisa saber o alvo para oferecer "Completar": no
  // cemiterial é o valor negociado, no funerário a entrada calculada.
  answerTargetRequest() {
    if (this.productTypeValue === "funerary_plan") {
      this.updateFuneraryEntryValue()
    } else {
      this._lastBroadcastCents = undefined
      this.broadcastNegotiated()
    }
  }

  // Só avisa quando o valor mudou: o editor do misto responde com "soma
  // mudou", que recalcula a cascata, que avisaria de novo — em ciclo, até
  // estourar a pilha (foi o "delay" de um segundo ao ligar o misto).
  broadcastNegotiated() {
    if (this.productTypeValue === "funerary_plan") return

    const cents = this.negotiatedCents()
    if (cents === this._lastBroadcastCents) return
    this._lastBroadcastCents = cents

    this.element.dispatchEvent(new CustomEvent("payment-fields:entry-changed", {
      bubbles: true,
      detail: { cents }
    }))
  }

  // Escreve um valor num campo com máscara sem disparar a cascata de novo.
  writeMoney(input, cents) {
    this._syncing = true
    input.value = "R$ " + (cents / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    input.dispatchEvent(new Event("input", { bubbles: true }))
    this._syncing = false
  }

  // Calculate total entry value: adesão + 1ª mensalidade (with beneficiaries, agregados, adicionals)
  calculateTotalEntry() {
    const adhesionAmount = this.adhesionAmountValue || 0

    return adhesionAmount + this.calculateMensalidade()
  }

  fillMaintenanceFee(event) {
    event.preventDefault()
    this.applyMaintenanceFee()
  }

  // A taxa nasce com a do produto: valor diferente do cadastro exige aprovação,
  // então o padrão é o cadastro. Só preenche campo vazio ou zerado — valor
  // digitado não se sobrescreve; para voltar existe o botão "Taxa Padrão".
  fillMaintenanceFeeDefault() {
    if (!this.hasMaintenanceTaxTarget) return

    const atual = this.parsePrice(this.maintenanceTaxTarget.value) || 0
    if (atual > 0) return

    this.applyMaintenanceFee()
  }

  applyMaintenanceFee() {
    if (this.hasMaintenanceTaxTarget && this.hasMaintenanceFeeValue) {
      // O input disparado aqui é para a máscara, não é o vendedor digitando.
      this._syncing = true
      this.maintenanceTaxTarget.value = this.maintenanceFeeValue
      this.maintenanceTaxTarget.dispatchEvent(new Event('input', { bubbles: true }))
      this._syncing = false
    }
    this.maintenanceDirty = false
  }

  // Fill adhesion discount with 100% of the entry value (Isentar 100%)
  fillAdhesionDiscount(event) {
    event.preventDefault()
    if (!this.hasDiscountAmountInputTarget) return

    // Calculate total entry: adesão + 1ª mensalidade
    const totalEntry = this.calculateTotalEntry()

    // Format the value in Brazilian currency format (without R$ prefix)
    const formattedValue = totalEntry.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })

    // Fill the input
    this.discountAmountInputTarget.value = formattedValue
    // Trigger input event for mask controller and field visibility updates
    this.discountAmountInputTarget.dispatchEvent(new Event('input', { bubbles: true }))
  }

  // Handle "Isento" payment method selection - auto-fill adhesion values
  handleIsentoSelection(event) {
    if (event.target.value !== "isento") return
    if (this.productTypeValue !== "funerary_plan") return

    // Calculate total entry: adesão + 1ª mensalidade
    const totalEntry = this.calculateTotalEntry()
    const formattedValue = totalEntry.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })

    // Fill down payment amount with total entry value (adesão + 1ª mensalidade)
    if (this.hasDownPaymentAmountTarget) {
      this.downPaymentAmountTarget.value = formattedValue
      this.downPaymentAmountTarget.dispatchEvent(new Event('input', { bubbles: true }))
    }

    // Fill discount amount with total entry value (100% exempt)
    if (this.hasDiscountAmountInputTarget) {
      this.discountAmountInputTarget.value = formattedValue
      this.discountAmountInputTarget.dispatchEvent(new Event('input', { bubbles: true }))
    }

    // Update visibility after filling values
    this.updateAdhesionFieldsVisibility()
  }

  // Clear values that were auto-filled by "Isento" when switching to another payment method
  clearIsentoValues() {
    // Clear discount amount (reset to 0)
    if (this.hasDiscountAmountInputTarget) {
      this.discountAmountInputTarget.value = "0,00"
      this.discountAmountInputTarget.dispatchEvent(new Event('input', { bubbles: true }))
    }

    // Clear down payment amount (reset to 0)
    if (this.hasDownPaymentAmountTarget) {
      this.downPaymentAmountTarget.value = "0,00"
      this.downPaymentAmountTarget.dispatchEvent(new Event('input', { bubbles: true }))
    }

    // Re-enable fields that were disabled by isento
    this.setIsentoFieldsDisabled(false)

    // Update visibility to show adhesion fields again
    this.updateAdhesionFieldsVisibility()
  }

  // Enable/disable fields when "Isento" is selected/deselected
  setIsentoFieldsDisabled(disabled) {
    const disabledClasses = ['bg-gray-100', 'cursor-not-allowed', 'opacity-60']

    // Disable/enable down payment amount input
    if (this.hasDownPaymentAmountTarget) {
      this.downPaymentAmountTarget.disabled = disabled
      this.downPaymentAmountTarget.readOnly = disabled
      if (disabled) {
        this.downPaymentAmountTarget.classList.add(...disabledClasses)
      } else {
        this.downPaymentAmountTarget.classList.remove(...disabledClasses)
      }
    }

    // Disable/enable discount amount input
    if (this.hasDiscountAmountInputTarget) {
      this.discountAmountInputTarget.disabled = disabled
      this.discountAmountInputTarget.readOnly = disabled
      if (disabled) {
        this.discountAmountInputTarget.classList.add(...disabledClasses)
      } else {
        this.discountAmountInputTarget.classList.remove(...disabledClasses)
      }
    }

    // Also disable/enable the fill price button (À Vista)
    if (this.hasFillPriceButtonTarget) {
      this.fillPriceButtonTarget.disabled = disabled
      if (disabled) {
        this.fillPriceButtonTarget.classList.add('opacity-50', 'cursor-not-allowed')
        this.fillPriceButtonTarget.classList.remove('hover:bg-blue-700', 'active:bg-blue-800')
      } else {
        this.fillPriceButtonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
        this.fillPriceButtonTarget.classList.add('hover:bg-blue-700', 'active:bg-blue-800')
      }
    }

    // Also disable/enable the fill discount button (Isentar 100%)
    if (this.hasFillDiscountButtonTarget) {
      this.fillDiscountButtonTarget.disabled = disabled
      if (disabled) {
        this.fillDiscountButtonTarget.classList.add('opacity-50', 'cursor-not-allowed')
        this.fillDiscountButtonTarget.classList.remove('hover:bg-green-700', 'active:bg-green-800')
      } else {
        this.fillDiscountButtonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
        this.fillDiscountButtonTarget.classList.add('hover:bg-green-700', 'active:bg-green-800')
      }
    }
  }

  getSelectedPaymentMethod() {
    // Radio desabilitado (misto ligado) ainda está "checked": ignorar, senão a
    // forma anterior manda mostrar Parcelas da Entrada com as formas ligadas.
    const radio = this.element.querySelector('input[name="direct_sale[down_payment_method]"]:checked:not([disabled])')
    if (radio) return radio.value
    // Misto: o split esconde os radios e usa um hidden field (habilitado quando
    // ligado). Reconhece "misto" como forma selecionada pra os campos de
    // financiamento (parcelas/1º vencimento) aparecerem com o split ligado.
    const misto = this.element.querySelector('input[type="hidden"][name="direct_sale[down_payment_method]"]:not([disabled])')
    return misto ? misto.value : null
  }

  // True quando o split (misto) está ligado (o hidden field "misto" habilitado).
  mistoEnabled() {
    return !!this.element.querySelector('input[type="hidden"][name="direct_sale[down_payment_method]"]:not([disabled])')
  }

  // Handle sale type change for funerary plans (Venda, Portabilidade, Reativação)
  updateSaleType(event) {
    const selectedType = event.target.value
    this.updateDiscountHint(selectedType)

    // Show payment method section when sale type is selected (for funerary plans)
    if (this.hasDownPaymentMethodSectionTarget && this.productTypeValue === "funerary_plan") {
      this.downPaymentMethodSectionTarget.classList.remove("hidden")
      // Scroll to payment method section
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.scrollToElement(this.downPaymentMethodSectionTarget)
        }, 150)
      })
    }
  }

  // Check if a sale type is selected (for funerary plans)
  isSaleTypeSelected() {
    const radio = this.element.querySelector('input[name="direct_sale[sale_type]"]:checked')
    return radio !== null
  }

  // Calculate and update the adhesion installment value display
  // Formula: (Adesão Total - Desconto) / parcelas
  // where Adesão Total = adesão_base + mensalidade_completa × parcelas
  updateAdhesionCalculation() {
    if (!this.hasAdhesionInstallmentValueTarget) return
    if (this.productTypeValue !== "funerary_plan") return

    const installments = this.hasAdhesionInstallmentsInputTarget
      ? parseInt(this.adhesionInstallmentsInputTarget.value) || 1
      : 1

    // Get discount amount
    const discountField = this.element.querySelector('input[name="direct_sale[discount_amount]"]')
    const discount = discountField ? this.parsePrice(discountField.value) : 0

    // Calculate Adesão Total = adesão_base + mensalidade_completa × parcelas
    const adesaoTotal = this.calculateAdesaoTotal(installments)

    // Calculate valor por parcela: (Adesão Total - Desconto) / parcelas
    const valorPorParcela = Math.max(0, (adesaoTotal - discount) / installments)

    // Update the display
    this.adhesionInstallmentValueTarget.textContent = this.formatCurrency(valorPorParcela)

    // Update the funerary entry value display
    this.updateFuneraryEntryValue()

    // Recalculate discount hint (max discount depends on adesão total)
    this.updateDiscountHint()

    // Update sticky bar
    this.updateStickyTotalEntry()
  }

  // Calculate and update the funerary entry value (auto-calculated)
  // Formula: Adesão Final = Adesão Total - Desconto
  // where Adesão Total = adesão_base + mensalidade × parcelas
  // Also calculates Valor da Entrada = Adesão Final ÷ Parcelas
  updateFuneraryEntryValue() {
    if (this.productTypeValue !== "funerary_plan") return

    const parcelas = this.getAdhesionInstallments()
    const adesaoTotal = this.calculateAdesaoTotal(parcelas)

    // Get discount
    const discountField = this.element.querySelector('input[name="direct_sale[discount_amount]"]')
    const discount = discountField ? this.parsePrice(discountField.value) : 0

    // Calculate: Adesão Final = Adesão Total - Desconto
    const adesaoFinal = Math.max(0, adesaoTotal - discount)

    // Calculate: Valor da Entrada (first installment) = Adesão Final ÷ Parcelas
    const valorEntrada = parcelas > 0 ? (adesaoFinal / parcelas) : adesaoFinal

    // Update the Adesão Final display value
    if (this.hasFuneraryEntryValueTarget) {
      this.funeraryEntryValueTarget.textContent = this.formatCurrency(adesaoFinal)
    }

    // Update the Valor da Entrada (first installment) display value
    if (this.hasFuneraryFirstInstallmentValueTarget) {
      this.funeraryFirstInstallmentValueTarget.textContent = this.formatCurrency(valorEntrada)
    }

    // Update the hidden manual input field for form submission
    if (this.hasDownPaymentAmountTarget) {
      // Format with comma decimal separator to match mask controller format
      this.downPaymentAmountTarget.value = this.formatPriceValue(adesaoFinal)
    }

    // Tell the multi-tender editor (payment-tenders controller) the live entry
    // target: for funerary the split must sum EXACTLY to "Valor da Entrada"
    // (= total_a_pagar), which changes as the operator edits the plan.
    const entryCents = Math.round(valorEntrada * 100)
    this.element.dispatchEvent(new CustomEvent("payment-fields:entry-changed", {
      bubbles: true,
      detail: { cents: entryCents }
    }))
  }

  // Format a number as a price string with comma decimal separator (e.g., 1909.70 -> "1.909,70")
  formatPriceValue(value) {
    return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  updateStickyTotalEntry() {
    if (!this.hasStickyTotalEntryTarget) return

    // Calculate valor por parcela WITH discount applied
    // Formula: (Adesão Total - Desconto) / parcelas
    const valorPorParcela = this.calculateTotalEntryWithDiscount()
    if (valorPorParcela > 0) {
      this.stickyTotalEntryTarget.classList.remove("hidden")
      if (this.hasStickyTotalEntryValueTarget) {
        this.stickyTotalEntryValueTarget.textContent = this.formatCurrency(valorPorParcela)
      }
    } else {
      this.stickyTotalEntryTarget.classList.add("hidden")
    }
  }

  // Calculate valor por parcela WITH discount: (Adesão Total - Desconto) / parcelas
  // where Adesão Total = adesão_base + mensalidade × parcelas
  calculateTotalEntryWithDiscount() {
    const parcelas = this.getAdhesionInstallments()
    const adesaoTotal = this.calculateAdesaoTotal(parcelas)

    // Get discount amount from form
    const discountField = this.element.querySelector('input[name="direct_sale[discount_amount]"]')
    const discount = discountField ? this.parsePrice(discountField.value) : 0

    const result = (adesaoTotal - discount) / parcelas
    return result > 0 ? result : 0
  }

  // Mensalidade completa, num lugar só — a mesma conta estava escrita três
  // vezes neste controller.
  calculateMensalidade() {
    // No modelo só-adesão, a primeira mensalidade já está na adesão.
    // Esta é a prévia local; a cotação confirmada continua sendo feita no servidor.
    if (this.pricingModelValue === "adhesion_only") return 0

    const { beneficiarios, agregados } = this.getBeneficiariesCounts()
    const chargeableBeneficiarios = Math.max(0, beneficiarios - this.includedBeneficiariesValue)
    const chargeableAgregados = Math.max(0, agregados - this.includedAgregadosValue)
    const beneficiariosCharge = chargeableBeneficiarios * this.beneficiaryPriceValue
    const agregadosCharge = chargeableAgregados * this.agregadoPriceValue

    return this.monthlyAmountValue + this.getAdicionalsTotal() + beneficiariosCharge + agregadosCharge
  }

  // Calculate Adesão Total = adesão_base + mensalidade × parcelas
  calculateAdesaoTotal(parcelas = 1) {
    const quoted = this.element.closest("[data-controller~='funerary-inline']")?.dataset.quoteGrossCents
    if (quoted !== undefined) return Number(quoted) / 100
    const adhesionAmount = this.adhesionAmountValue || 0

    return adhesionAmount + (this.calculateMensalidade() * parcelas)
  }

  getAdhesionInstallments() {
    return this.hasAdhesionInstallmentsInputTarget
      ? parseInt(this.adhesionInstallmentsInputTarget.value) || 1
      : 1
  }

  updateDiscountHint(saleType = null) {
    // Get current sale type if not provided
    if (!saleType) {
      const saleTypeRadio = this.element.querySelector('input[name="direct_sale[sale_type]"]:checked')
      saleType = saleTypeRadio?.value || "venda"
    }

    // Get current discount amount
    const discountField = this.element.querySelector('input[name="direct_sale[discount_amount]"]')
    const currentDiscount = discountField ? this.parsePrice(discountField.value) : 0

    // Check portability discount limit
    const isPortability = saleType === "portabilidade"
    const isFuneraryPlan = this.productTypeValue === "funerary_plan"
    const installments = this.getAdhesionInstallments()
    const adesaoTotal = this.calculateAdesaoTotal(installments)
    const hasPortabilityLimit = isPortability && this.portabilityDiscountLimitValue > 0 && adesaoTotal > 0
    const maxPortabilityDiscount = hasPortabilityLimit ? (adesaoTotal * this.portabilityDiscountLimitValue / 100) : 0

    // Check seller pre-approved discount limit
    const hasSellerLimit = this.sellerDiscountLimitValueValue > 0 && this.sellerDiscountLimitTypeValue !== ""
    let maxSellerDiscount = 0
    if (hasSellerLimit) {
      if (this.sellerDiscountLimitTypeValue === "percentage") {
        const baseAmount = isFuneraryPlan ? adesaoTotal : (this.referenceCents() / 100)
        maxSellerDiscount = baseAmount > 0 ? (baseAmount * this.sellerDiscountLimitValueValue / 100) : 0
      } else {
        maxSellerDiscount = this.sellerDiscountLimitValueValue
      }
    }

    // Determine the most generous applicable limit
    const applicableLimits = []
    if (hasPortabilityLimit) applicableLimits.push({ type: "portability", max: maxPortabilityDiscount })
    if (hasSellerLimit && maxSellerDiscount > 0) applicableLimits.push({ type: "seller", max: maxSellerDiscount })
    const bestLimit = applicableLimits.length > 0
      ? applicableLimits.reduce((best, current) => current.max > best.max ? current : best)
      : null

    // Hide all hints first
    if (this.hasDiscountHintDefaultTarget) this.discountHintDefaultTarget.classList.add("hidden")
    if (this.hasDiscountHintPortabilityTarget) this.discountHintPortabilityTarget.classList.add("hidden")
    if (this.hasDiscountHintSellerTarget) this.discountHintSellerTarget.classList.add("hidden")
    if (this.hasDiscountHintExceededTarget) this.discountHintExceededTarget.classList.add("hidden")

    if (bestLimit) {
      const withinLimit = currentDiscount <= 0 || currentDiscount <= bestLimit.max

      if (!withinLimit) {
        // Discount exceeds the most generous limit - show warning
        if (this.hasDiscountHintExceededTarget) {
          const limitLabel = bestLimit.type === "portability"
            ? `limite de portabilidade ${this.formatCurrency(bestLimit.max)} (${this.portabilityDiscountLimitValue}%)`
            : `limite pré-aprovado ${this.formatCurrency(bestLimit.max)}`
          this.discountHintExceededTarget.textContent = `Desconto excede o ${limitLabel} - requer aprovação`
          this.discountHintExceededTarget.classList.remove("hidden")
        }
      } else {
        // Within limit - show green message for the best applicable limit
        if (bestLimit.type === "portability" && this.hasDiscountHintPortabilityTarget) {
          this.discountHintPortabilityTarget.textContent = `Portabilidade: desconto até ${this.formatCurrency(bestLimit.max)} (${this.portabilityDiscountLimitValue}%) não requer aprovação`
          this.discountHintPortabilityTarget.classList.remove("hidden")
        } else if (bestLimit.type === "seller" && this.hasDiscountHintSellerTarget) {
          const limitDesc = this.sellerDiscountLimitTypeValue === "percentage"
            ? `${this.sellerDiscountLimitValueValue}%`
            : this.formatCurrency(this.sellerDiscountLimitValueValue)
          this.discountHintSellerTarget.textContent = `Desconto pré-aprovado até ${this.formatCurrency(bestLimit.max)} (${limitDesc}) não requer aprovação`
          this.discountHintSellerTarget.classList.remove("hidden")
        }

        // If there's also a secondary limit, show it as additional info
        if (applicableLimits.length > 1) {
          const secondLimit = applicableLimits.find(l => l.type !== bestLimit.type)
          if (secondLimit && secondLimit.type === "seller" && this.hasDiscountHintSellerTarget && this.discountHintSellerTarget.classList.contains("hidden")) {
            const limitDesc = this.sellerDiscountLimitTypeValue === "percentage"
              ? `${this.sellerDiscountLimitValueValue}%`
              : this.formatCurrency(this.sellerDiscountLimitValueValue)
            this.discountHintSellerTarget.textContent = `Pré-aprovado: até ${this.formatCurrency(secondLimit.max)} (${limitDesc})`
            this.discountHintSellerTarget.classList.remove("hidden")
          } else if (secondLimit && secondLimit.type === "portability" && this.hasDiscountHintPortabilityTarget && this.discountHintPortabilityTarget.classList.contains("hidden")) {
            this.discountHintPortabilityTarget.textContent = `Portabilidade: até ${this.formatCurrency(secondLimit.max)} (${this.portabilityDiscountLimitValue}%)`
            this.discountHintPortabilityTarget.classList.remove("hidden")
          }
        }
      }
    } else {
      // No limits - show default warning
      if (this.hasDiscountHintDefaultTarget) {
        this.discountHintDefaultTarget.classList.remove("hidden")
      }
    }
  }

  togglePaymentSection() {
    if (!this.hasPaymentSectionTarget || !this.hasNoProductMessageTarget) {
      return
    }

    const selectedProduct = this.element.querySelector('input[name="direct_sale[product_id]"]:checked')

    if (selectedProduct) {
      // Product selected - show payment section, hide message
      this.paymentSectionTarget.classList.remove('hidden')
      this.noProductMessageTarget.classList.add('hidden')
    } else {
      // No product selected - hide payment section, show message
      this.paymentSectionTarget.classList.add('hidden')
      this.noProductMessageTarget.classList.remove('hidden')
    }
  }

  updateStickySummary(productElement) {
    if (!this.hasStickyProductSummaryTarget) return

    if (productElement) {
      // Mark that a product is selected
      this.productSelected = true

      // Get product info from data attributes
      const productName = productElement.dataset.productName || 'Produto'
      const productType = productElement.dataset.productType || ''
      const productPrice = productElement.dataset.productPrice || '0,00'
      const adhesionAmount = productElement.dataset.productAdhesionAmount

      // Update product name
      if (this.hasStickyProductNameTarget) {
        this.stickyProductNameTarget.textContent = productName
      }

      // Update product type badge
      if (this.hasStickyProductTypeTarget) {
        const isFunerary = productType === 'funerary_plan'
        this.stickyProductTypeTarget.textContent = isFunerary ? 'Funerária' : 'Cemitério'
        this.stickyProductTypeTarget.className = isFunerary
          ? 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800'
          : 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800'
      }

      // Update price label and value
      if (this.hasStickyProductPriceLabelTarget) {
        this.stickyProductPriceLabelTarget.textContent = productType === 'funerary_plan' ? 'Adesão:' : 'Preço:'
      }

      if (this.hasStickyProductPriceTarget) {
        // For funerary plans, show adhesion amount; for cemetery, show price
        const displayPrice = productType === 'funerary_plan' && adhesionAmount ? adhesionAmount : productPrice
        this.stickyProductPriceTarget.textContent = `R$ ${displayPrice}`
      }

      // Show/hide total entry for funerary plans
      if (productType === 'funerary_plan') {
        this.updateStickyTotalEntry()
      } else if (this.hasStickyTotalEntryTarget) {
        this.stickyTotalEntryTarget.classList.add("hidden")
      }
    } else {
      // Mark that no product is selected
      this.productSelected = false
      // Hide the sticky bar
      this.hideStickyBar()
      this.stickyProductSummaryTarget.classList.add('hidden')
    }

    // Check scroll position to show/hide bar
    this.handleScroll()
  }

  // Update dynamic labels based on product type (funerary vs cemetery)
  updateDynamicLabels(productType) {
    const isFunerary = productType === "funerary_plan"

    // Update "Forma de Pagamento da Entrada" label
    if (this.hasDownPaymentMethodLabelTarget) {
      this.downPaymentMethodLabelTarget.textContent = isFunerary
        ? "Forma de Pagamento da Entrada da Adesão"
        : "Forma de Pagamento da Entrada"
    }

    // Update "Valor da Entrada" label
    if (this.hasDownPaymentAmountLabelTarget) {
      this.downPaymentAmountLabelTarget.textContent = isFunerary
        ? "Valor da Entrada da Adesão (R$)"
        : "Entrada: o que o cliente paga agora (R$)"
    }

    // Update "Parcelas da Entrada" label
    if (this.hasDownPaymentInstallmentsLabelTarget) {
      this.downPaymentInstallmentsLabelTarget.textContent = isFunerary
        ? (this.pricingModelValue === "adhesion_only" ? "Parcelas da adesão" : "Parcelas da Entrada da Adesão")
        : "Parcelas da Entrada"
    }

    // Update "Data da Entrada" label
    if (this.hasDownPaymentDateLabelTarget) {
      this.downPaymentDateLabelTarget.textContent = isFunerary
        ? "Data da Entrada da Adesão"
        : "Data da Entrada"
    }

    // Show/hide "Isento" option and adjust grid (funerary only)
    if (this.hasIsentoOptionTarget) {
      if (isFunerary) {
        this.isentoOptionTarget.classList.remove("hidden")
      } else {
        this.isentoOptionTarget.classList.add("hidden")
        // If isento was selected and we're switching to cemetery, uncheck it
        const isentoRadio = this.isentoOptionTarget.querySelector('input[type="radio"]')
        if (isentoRadio && isentoRadio.checked) {
          isentoRadio.checked = false
        }
      }
    }

    // Adjust payment method grid columns based on product type
    if (this.hasPaymentMethodGridTarget) {
      if (isFunerary) {
        // 5 options for funerary (Isento, PIX, POS, Cartão, Dinheiro)
        this.paymentMethodGridTarget.classList.remove("sm:grid-cols-4")
        this.paymentMethodGridTarget.classList.add("sm:grid-cols-5")
      } else {
        // 4 options for cemetery (PIX, POS, Cartão, Dinheiro)
        this.paymentMethodGridTarget.classList.remove("sm:grid-cols-5")
        this.paymentMethodGridTarget.classList.add("sm:grid-cols-4")
      }
    }
  }

  // Update the product info panel with product details
  updateProductInfoPanel(productElement) {
    if (!this.hasProductInfoPanelTarget) return

    if (!productElement) {
      this.productInfoPanelTarget.classList.add("hidden")
      return
    }

    const productType = productElement.dataset.productType

    // Show the panel and the appropriate sub-panel
    this.productInfoPanelTarget.classList.remove("hidden")

    if (productType === "funerary_plan") {
      // Show funerary panel, hide cemetery
      if (this.hasFuneraryInfoPanelTarget) this.funeraryInfoPanelTarget.classList.remove("hidden")
      if (this.hasCemeteryInfoPanelTarget) this.cemeteryInfoPanelTarget.classList.add("hidden")

      // Populate funerary info
      if (this.hasInfoAdhesionAmountTarget) {
        const adhesionAmount = productElement.dataset.productAdhesionAmount
        this.infoAdhesionAmountTarget.textContent = adhesionAmount ? `R$ ${adhesionAmount}` : "-"
      }
      if (this.hasInfoMonthlyAmountTarget) {
        this.infoMonthlyAmountTarget.textContent = productElement.dataset.monthlyAmountFormatted || "-"
      }
      if (this.hasInfoMaxAdhesionInstallmentsTarget) {
        const maxInstallments = productElement.dataset.maxAdhesionInstallments
        this.infoMaxAdhesionInstallmentsTarget.textContent = maxInstallments ? `${maxInstallments}x` : "-"
      }
      if (this.hasInfoMaxDownPaymentInstallmentsTarget) {
        const maxDownPaymentInstallments = productElement.dataset.maxDownPaymentInstallments
        this.infoMaxDownPaymentInstallmentsTarget.textContent = maxDownPaymentInstallments ? `${maxDownPaymentInstallments}x` : "-"
      }
      if (this.hasInfoIncludedBeneficiariesTarget) {
        this.infoIncludedBeneficiariesTarget.textContent = productElement.dataset.includedBeneficiaries || "0"
      }
      if (this.hasInfoBeneficiaryPriceTarget) {
        this.infoBeneficiaryPriceTarget.textContent = productElement.dataset.beneficiaryPriceFormatted || "-"
      }
      if (this.hasInfoIncludedAgregadosTarget) {
        this.infoIncludedAgregadosTarget.textContent = productElement.dataset.includedAgregados || "0"
      }
      if (this.hasInfoAgregadoPriceTarget) {
        this.infoAgregadoPriceTarget.textContent = productElement.dataset.agregadoPriceFormatted || "-"
      }
      if (this.hasInfoGraceBeneficiariesTarget) {
        this.infoGraceBeneficiariesTarget.textContent = this.formatGracePeriod(
          productElement.dataset.gracePeriodDays,
          productElement.dataset.gracePeriodInstallments,
          productElement.dataset.graceMode
        )
      }
      if (this.hasInfoGraceAgregadosTarget) {
        this.infoGraceAgregadosTarget.textContent = this.formatGracePeriod(
          productElement.dataset.gracePeriodDaysAgregados,
          productElement.dataset.gracePeriodInstallmentsAgregados,
          productElement.dataset.graceMode
        )
      }
    } else {
      // Show cemetery panel, hide funerary
      if (this.hasFuneraryInfoPanelTarget) this.funeraryInfoPanelTarget.classList.add("hidden")
      if (this.hasCemeteryInfoPanelTarget) this.cemeteryInfoPanelTarget.classList.remove("hidden")

      // Populate cemetery info
      if (this.hasInfoMaintenanceFeeTarget) {
        const fee = productElement.dataset.productMaintenanceFee
        this.infoMaintenanceFeeTarget.textContent = fee ? `R$ ${fee}` : "-"
      }
      if (this.hasInfoFinancialGraceTarget) {
        const grace = productElement.dataset.financialGrace
        this.infoFinancialGraceTarget.textContent = grace ? `${grace}%` : "-"
      }
      if (this.hasInfoInterestRateTarget) {
        const rate = productElement.dataset.interestRate
        this.infoInterestRateTarget.textContent = rate ? `${rate}%` : "-"
      }
      if (this.hasInfoInstallmentsNumberTarget) {
        const installments = productElement.dataset.installmentsNumber
        this.infoInstallmentsNumberTarget.textContent = installments ? `${installments}x` : "-"
      }
      if (this.hasInfoGraceDaysTarget) {
        const days = productElement.dataset.gracePeriodDays
        this.infoGraceDaysTarget.textContent = days ? `${days} dias` : "-"
      }
    }
  }

  // Format grace period for display
  formatGracePeriod(days, installments, mode) {
    if (!days && !installments) return "-"

    const parts = []
    if (days) parts.push(`${days} dias`)
    if (installments) parts.push(`${installments} parcelas`)

    if (parts.length === 0) return "-"
    if (parts.length === 1) return parts[0]

    // If both are present, show based on mode
    switch (mode) {
      case "days_only":
        return `${days} dias`
      case "installments_only":
        return `${installments} parcelas`
      case "days_or_installments":
        return `${parts.join(" ou ")}`
      case "days_and_installments":
        return `${parts.join(" e ")}`
      default:
        return parts.join(" / ")
    }
  }

  // Calculate and update the monthly subtotal for the first monthly discount section
  calculatePricePreview() {
    if (this.productTypeValue !== "funerary_plan") return

    const mensalidade = this.calculateMensalidade()

    // Update the subtotal display
    if (this.hasFirstMonthlySubtotalDisplayTarget) {
      this.firstMonthlySubtotalDisplayTarget.textContent = this.formatCurrency(mensalidade)
    }

    // Update all related calculations (adhesion installment value, entry value, etc.)
    this.updateAdhesionCalculation()
    this.updateFuneraryEntryValue()

    // Update sticky total entry
    this.updateStickyTotalEntry()
  }

  getBeneficiariesCounts() {
    let beneficiarios = 0
    let agregados = 0

    const people = this.element.querySelector("[data-funerary-inline-target='people']")
    if (people) {
      people.querySelectorAll("[data-person-row]").forEach(row => {
        if (row.hidden || row.querySelector("[data-destroy]")?.value === "1") return
        const type = row.querySelector("[name$='[beneficiary_type]']")?.value
        if (type === "beneficiario") beneficiarios += 1
        if (type === "agregado") agregados += 1
      })
      return { beneficiarios, agregados }
    }

    if (!this.hasBeneficiariesSectionTarget) {
      return { beneficiarios, agregados }
    }

    // First, try to find the new simplified beneficiaries-quantity controller
    const quantityElement = this.beneficiariesSectionTarget.querySelector("[data-controller='beneficiaries-quantity']")
    if (quantityElement) {
      const quantityController = this.application.getControllerForElementAndIdentifier(
        quantityElement,
        "beneficiaries-quantity"
      )
      if (quantityController) {
        beneficiarios = quantityController.getBeneficiariesCount ? quantityController.getBeneficiariesCount() : 0
        agregados = quantityController.getAgregadosCount ? quantityController.getAgregadosCount() : 0
        return { beneficiarios, agregados }
      }

      // Fallback: read directly from inputs if controller not available
      const beneficiariesInput = quantityElement.querySelector("[data-beneficiaries-quantity-target='beneficiariesInput']")
      const agregadosInput = quantityElement.querySelector("[data-beneficiaries-quantity-target='agregadosInput']")
      if (beneficiariesInput) beneficiarios = parseInt(beneficiariesInput.value) || 0
      if (agregadosInput) agregados = parseInt(agregadosInput.value) || 0
      return { beneficiarios, agregados }
    }

    // Fallback: find the old beneficiaries controller (for backward compatibility)
    const beneficiariesElement = this.beneficiariesSectionTarget.querySelector("[data-controller='beneficiaries']")
    if (beneficiariesElement) {
      const beneficiariesController = this.application.getControllerForElementAndIdentifier(
        beneficiariesElement,
        "beneficiaries"
      )
      if (beneficiariesController && typeof beneficiariesController.getCounts === "function") {
        const counts = beneficiariesController.getCounts()
        beneficiarios = counts.beneficiarios || 0
        agregados = counts.agregados || 0
      }
    }

    return { beneficiarios, agregados }
  }

  getAdicionalsTotal() {
    if (!this.hasAdicionaisSectionTarget) return 0

    // Find the adicionals controller
    const adicionalsElement = this.adicionaisSectionTarget.querySelector("[data-controller='adicionals']")
    if (!adicionalsElement) return 0

    // Calculate total from visible adicional items
    const container = adicionalsElement.querySelector("[data-adicionals-target='container']")
    if (!container) return 0

    const items = container.querySelectorAll("[data-adicional-item]:not(.hidden)")
    let total = 0

    items.forEach(item => {
      const quantityInput = item.querySelector("input[name*='quantity']")
      const unitPrice = parseFloat(item.dataset.unitPrice) || 0
      const quantity = parseInt(quantityInput?.value) || 1
      total += unitPrice * quantity
    })

    return total
  }

  parsePrice(value) {
    if (!value) return 0
    // Handle Brazilian format: 1.234,56 -> 1234.56
    const cleaned = String(value)
      .replace("R$", "")
      .trim()
      .replace(/\./g, "")
      .replace(",", ".")
    return parseFloat(cleaned) || 0
  }

  formatCurrency(value) {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL"
    }).format(value)
  }

  // Update visibility of adhesion-related fields when down payment or discount changes
  updateAdhesionFieldsVisibility() {
    if (this.productTypeValue !== "funerary_plan") return

    const hasRemainingAdhesion = !this.isAdhesionFullyCovered()
    const isFullyDiscounted = this.isAdhesionFullyDiscounted()

    // Show/hide adhesion exempt notice
    if (this.hasAdhesionExemptNoticeTarget) {
      if (isFullyDiscounted) {
        this.adhesionExemptNoticeTarget.classList.remove("hidden")
      } else {
        this.adhesionExemptNoticeTarget.classList.add("hidden")
      }
    }

    // Payment method section should ALWAYS remain visible
    // Even when fully discounted, user should be able to select/change payment method
    // NOTE: We do NOT clear the radio selection - this preserves the user's choice
    if (this.hasDownPaymentMethodSectionTarget) {
      const paymentMethodRadios = this.element.querySelectorAll('input[name="direct_sale[down_payment_method]"]')
      // Always keep payment method section visible
      this.downPaymentMethodSectionTarget.classList.remove("hidden")
      // Restore required on first radio
      if (paymentMethodRadios.length > 0) {
        paymentMethodRadios[0].setAttribute("required", "required")
      }
    }

    // Down payment amount field should remain visible - user entered the value
    // The "net to pay = 0" will be shown in the simulation/summary
    // We don't hide or zero it here

    this.syncDownPaymentDateVisibility()

    // Show/hide down payment installments based on discount state and payment method
    // Note: PIX and cash don't support installments
    if (this.hasDownPaymentInstallmentsTarget) {
      const selectedMethod = this.getSelectedPaymentMethod()
      const hasPaymentMethod = selectedMethod !== null
      const supportsInstallments = selectedMethod && !['pix', 'cash', 'isento'].includes(selectedMethod)

      if (isFullyDiscounted || !supportsInstallments) {
        this.downPaymentInstallmentsTarget.classList.add("hidden")
      } else if (hasPaymentMethod) {
        this.downPaymentInstallmentsTarget.classList.remove("hidden")
      }
    }

    if (this.hasAdhesionInstallmentsFieldTarget) {
      const hasPaymentMethod = this.getSelectedPaymentMethod() !== null
      // Só adesão mantém uma única cobrança contratual; parcelas ficam no recebimento.
      if (this.pricingModelValue !== "adhesion_only" && hasPaymentMethod && !isFullyDiscounted) {
        this.adhesionInstallmentsFieldTarget.classList.remove("hidden")
        // Set min to 1 (user can pay in a single installment)
        if (this.hasAdhesionInstallmentsInputTarget) {
          this.adhesionInstallmentsInputTarget.setAttribute("min", 1)
          if (this.maxAdhesionInstallmentsValue > 0) {
            this.adhesionInstallmentsInputTarget.setAttribute("max", this.maxAdhesionInstallmentsValue)
          }
        }
        if (this.hasAdhesionInstallmentsMaxTarget && this.maxAdhesionInstallmentsValue > 0) {
          this.adhesionInstallmentsMaxTarget.textContent = `(máx: ${this.maxAdhesionInstallmentsValue})`
        }
        this.updateAdhesionCalculation()
      } else {
        this.adhesionInstallmentsFieldTarget.classList.add("hidden")
        // Reset to 1 when hidden
        if (this.hasAdhesionInstallmentsInputTarget) {
          this.adhesionInstallmentsInputTarget.value = 1
          this.adhesionInstallmentsInputTarget.setAttribute("min", 1)
        }
      }
    }

    // Update other funerary sections based on full discount state
    if (isFullyDiscounted) {
      // Still show beneficiaries and adicionals even with full discount
      // They affect the mensalidade, not the adhesion
      this.toggleFuneraryPlanSectionsForFullDiscount()
    }
  }

  // Special handling when adhesion is fully discounted (no payment needed)
  toggleFuneraryPlanSectionsForFullDiscount() {
    // Contract first due date should still be shown - mensalidade still applies
    if (this.hasContractFirstDueDateFieldTarget) {
      this.contractFirstDueDateFieldTarget.classList.remove("hidden")
    }

    // Beneficiaries section should still be shown
    if (this.hasBeneficiariesSectionTarget && this.allowsBeneficiariesValue) {
      this.beneficiariesSectionTarget.classList.remove("hidden")
    }

    // Adicionals section should still be shown (but don't clear user selections)
    if (this.hasAdicionaisSectionTarget) {
      this.adicionaisSectionTarget.classList.remove("hidden")
      // Note: Don't call updateAdicionalsFromProduct() here - it would clear user selections
    }
  }

  // Check if discount covers the entire adhesion (nothing to pay upfront)
  // Adhesion total = adesao_base + (mensalidade × parcelas)
  // A visibilidade da Data da Entrada morava em dois lugares, com condições
  // escritas ao contrário uma da outra — e a última a rodar ganhava. Ao desligar
  // o split, a data ficava sobrando na tela sem forma de pagamento nenhuma.
  // Uma regra só, chamada por último: sem forma escolhida, não há data.
  syncDownPaymentDateVisibility() {
    if (!this.hasDownPaymentDateFieldTarget) return

    const mostra = this.getSelectedPaymentMethod() !== null || this.isAdhesionFullyDiscounted()
    this.downPaymentDateFieldTarget.classList.toggle("hidden", !mostra)
  }

  isAdhesionFullyDiscounted() {
    if (this.productTypeValue !== "funerary_plan") return false

    const adhesionBase = this.adhesionAmountValue || 0
    if (adhesionBase <= 0) return false

    // Get the number of adhesion installments
    const parcelas = this.getAdhesionInstallments()

    // Calculate total adhesion (base + mensalidade × parcelas)
    const adesaoTotal = this.calculateAdesaoTotal(parcelas)

    // Get discount amount
    const discountField = this.element.querySelector('input[name="direct_sale[discount_amount]"]')
    const discount = discountField ? this.parsePrice(discountField.value) : 0

    // Check if discount covers the total adhesion (with small tolerance for floating point)
    return discount >= (adesaoTotal - 0.01)
  }

  // Check if adhesion is fully covered by down payment + discount
  // Returns true if (entrada + desconto) >= adesão
  isAdhesionFullyCovered() {
    const adhesion = this.adhesionAmountValue || 0
    if (adhesion <= 0) return true

    // Get down payment amount
    const downPayment = this.hasDownPaymentAmountTarget
      ? this.parsePrice(this.downPaymentAmountTarget.value)
      : 0

    // Get discount amount
    const discountField = this.element.querySelector('input[name="direct_sale[discount_amount]"]')
    const discount = discountField ? this.parsePrice(discountField.value) : 0

    // Check if entrada + desconto covers the adhesion (with small tolerance for floating point)
    return (downPayment + discount) >= (adhesion - 0.01)
  }

}
